/**
 * English interface strings.
 *
 * Two groups here are quoted verbatim from ux.md and should not be reworded casually:
 *
 * - `sync.*` — the plain-language sync states. The same five words appear on every surface, on
 *   web and native, so a volunteer learns them once. Never a bare icon, never a silent failure.
 * - `registration.state.*` — a closed window explains what to do next rather than presenting a
 *   dead end.
 */

import type { Messages } from "../index.js";

export const enCA: Messages = {
  // Sync — ux.md §2, "Never block on the network".
  "sync.savedLocally": "Saved on this device",
  "sync.sending": "Sending…",
  "sync.sent": "Sent",
  "sync.failed": "Couldn't send — will retry",
  "sync.conflict": "Needs your review",
  "sync.pendingCount": "{count} waiting to send",
  "sync.offline": "No signal — you can keep working",

  // Registration state — ux.md §3.1.
  "registration.state.opensAt": "Registration opens {when}",
  "registration.state.closesIn": "Registration closes {relative}",
  "registration.state.open": "Registration is open",
  "registration.state.closed": "Registration is closed — see the desk at the gate",
  "registration.state.notScheduled": "Registration times will be announced",
  "registration.override.prompt": "Registration closed at {when} — register anyway?",
  "registration.override.reasonLabel": "Reason for registering after close",

  // Check-in — ux.md §3.4.
  "checkIn.already": "Already checked in at {when}",
  "checkIn.confirmed": "Checked in",
  "checkIn.undo": "Undo",
  "checkIn.counter": "{checkedIn} checked in · {expected} expected",

  // Judging — ux.md §3.6.
  "judge.signInPrompt": "Enter your access code",
  "judge.abstain": "Abstain",
  "judge.abstainReason": "Why are you abstaining?",
  "judge.nextDancer": "Next dancer",
  "judge.runningTotal": "Total",
  "judge.blindMode": "Names are hidden for this competition",

  // Money — ux.md §3.9.
  "payout.notYetPayable": "Not yet payable — awaiting approved results for this category",
  "payout.reasonRequired": "Reason",
  "payout.void": "Void this payout",

  // Shared confirmations.
  "action.undo": "Undo",
  "action.cancel": "Cancel",
  "action.save": "Save",
  "action.print": "Print",
  "common.reasonRequired": "A reason is required, and it is recorded with your name.",
  "common.offlineNotice": "You are offline. Everything you do is saved and will send later.",

  // Error envelope — the words the API's error codes render as.
  "error.notFound": "We could not find that.",
  "error.forbidden": "You do not have permission to do that.",
  "error.conflict": "Someone else changed this while you were working.",
  "error.validation": "Something on this form needs fixing.",
  "error.unavailable": "That is not working right now. Your work is saved.",
  /** A read that could not reach the API at all. Nothing was being saved, so nothing is promised. */
  "error.tryAgain": "That is not working right now. Please try again in a moment.",

  /* ------------------------------------------------------------ the public site
   *
   * Everything below is a string the *site* writes. The community's own words — event names,
   * competition and category names, announcement bodies, `info_json` prose, the labels on the
   * organiser's registration form — are never keys, because they arrive from the database in
   * whatever language the community wrote them in.
   */

  // The shell, and the platform's own page.
  "site.skipToContent": "Skip to content",
  "site.language": "Language",
  "site.community.description": "Events for {community}",
  /* The section links in the header, platform page only — a community's page has one subject and
     nothing to navigate. Each is short enough to survive a phone at the width where the bar drops
     them onto their own line. */
  "site.nav.label": "Site sections",
  "site.nav.whatsOn": "What's on",
  "site.nav.communities": "Communities",
  "site.nav.organisers": "For organisers",
  /* The footer. `operatedBy` labels the owner's mark rather than restating the copyright line
     beside it, which already names the same company. */
  "site.footer.operatedBy": "Operated by",
  "site.legal.privacy": "Privacy",
  "site.legal.terms": "Terms of use",
  "site.legal.dataGovernance": "Data governance",
  /* A policy with no date is a policy with no history — the reader cannot tell whether what they
     agreed to last year is what is on the screen now. */
  "site.legal.updated": "Last updated {date}",
  /* The line under the mark on the platform's own page, and its `<meta description>`. It names
     what a visitor is looking for, not what the software is — the software's own description is
     `site.platform.blurb`, further down that page. */
  "site.platform.tagline": "Find a pow wow — dates, programme, registration, and results",
  "site.platform.blurb":
    "Event and competition management designed for how a pow wow actually runs: multiple sessions rather than one deadline, judging panels set on the day, and prize rules that each community defines for itself.",
  "site.platform.noCommunities": "No communities are listed yet.",
  "site.platform.lead":
    "A pow wow is a community gathering of dancing, drumming, food, family, and cultural celebration. This is where participating communities publish event dates, locations, registration details, schedule information, and approved results.",

  /* ------------------------------------------------------- the platform's front page (D72)
   *
   * Two audiences on one page, and they arrive in this order: somebody looking for a pow wow to
   * go to, then somebody wondering what a pow wow is, then a community wondering whether to run
   * theirs here. The wording never sells — a visitor is looking for a date and a place, and an
   * organiser can tell whether the software understands their weekend from how it describes it.
   */

  /* The pictures at the top. `note` is on the page because these are generated illustrations
     rather than photographs of any particular gathering, and a page about real events should not
     leave a reader to assume otherwise. Replace it when real, permissioned photographs land. */
  "platform.hero.label": "Pictures of a pow wow",
  "platform.hero.note": "Illustrations — not photographs of any particular gathering.",
  "platform.hero.alt.grandEntry":
    "Dancers entering the arena in procession for the Grand Entry, led by an eagle staff and flags.",
  "platform.hero.alt.arbour":
    "The arbour and the dance circle seen from the stands, with families seated around it.",
  "platform.hero.alt.drum": "A drum group seated around a large drum, singing.",
  "platform.hero.alt.dancers":
    "Three dancers on the grass mid-dance, seen from behind, shawls and fringe in motion.",
  "platform.hero.alt.grounds":
    "The grounds around the arena on a summer afternoon — vendor tents, families, children.",

  "platform.upcoming.heading": "Pow wows coming up",
  "platform.upcoming.intro":
    "Every event published by a community on this platform is listed here, soonest first. Each listing links to the community's own page, where visitors can review the programme, registration information, public updates, and approved results.",
  "platform.upcoming.none":
    "No events have been published for the coming season yet. When a community publishes an event, it appears here at the same time it appears on that community's own page, so the information only needs to be maintained once.",
  "platform.upcoming.hostedBy": "Hosted by {community}",
  "platform.upcoming.browse": "Browse the communities",

  "platform.directory.heading": "Find a community",
  "platform.directory.intro":
    "{count} communities have a page here. Open a community page to see its programme, registration information, public updates, and results. A community with no published event yet still has a page, ready for its first listing.",
  /* The count is one on a fresh deployment and on a single-community install, and *1 communities
     have a page* is the first thing a reader sees there. A second string rather than a plural rule,
     because the verb inflects too and so does the French. */
  "platform.directory.introOne":
    "One community has a page here. Open it to see its programme, registration information, public updates, and results. A community with no published event yet still has a page, ready for its first listing.",
  /* Neutral in both states: a `summary` shows one label whether the list is open or closed, and
     the browser's own marker is what says which. */
  "platform.directory.showAll": "All {count} communities",
  "platform.directory.showAllOne": "The one community",
  "platform.directory.filter.label": "Search by name",
  "platform.directory.filter.placeholder": "Type any part of a name",
  "platform.directory.filter.showing": "Showing {shown} of {total}.",
  "platform.directory.filter.none": "No community matches “{query}”.",
  "platform.directory.jump": "Jump to a letter",
  "platform.directory.nextEvent": "Next: {event}, {when}",

  "platform.about.heading": "What happens at a pow wow",
  "platform.about.what":
    "A pow wow is a gathering where First Nations and Native American communities come together to dance, sing, visit, and honour their cultures. Some run for a single afternoon and others continue for several days. Some include competitions with prize money; others are traditional pow wows, held for the gathering itself rather than for a placing.",
  "platform.about.name":
    "The word comes from the Narragansett powwaw, “spiritual leader”. Public gatherings in something close to the present form spread from the Great Plains in the late 1800s and early 1900s — through decades in which governments worked to stop them, and communities went on holding them anyway.",
  "platform.about.grandEntry":
    "A pow wow opens with the Grand Entry: the Eagle Staff first, then the flags, then the dancers, with a host drum singing them in. Veterans commonly carry the staff and the flags. An invocation, a Flag Song and a Veterans' Song follow before the dancing begins.",
  "platform.about.dances":
    "The dancing then runs by category — Men's Traditional, Grass and Fancy; Women's Traditional, Jingle Dress and Fancy Shawl — usually repeated down through teen, junior and tiny tot age groups, with intertribals in between that anyone present may join.",
  "platform.about.staff":
    "The head staff hold the day together: an MC who is the voice of the whole gathering, an arena director, head dancers who lead every dance onto the floor, and host drums whose songs the dancing is built on.",
  "platform.about.etiquetteHeading": "If you are going",
  "platform.about.etiquette.regalia":
    "What a dancer wears is regalia, never a costume. Much of it is handmade, and some of it is inherited.",
  "platform.about.etiquette.photos":
    "Ask before photographing, and expect parts of the day to be off limits entirely. The MC will say which.",
  "platform.about.etiquette.drum":
    "The drum is treated as a being in its own right. Don't touch it, or anyone's regalia, without being asked to.",
  "platform.about.etiquette.mc":
    "Listen to the MC. Standing for the Grand Entry, for flag songs and for honour songs is the one instruction that covers most of the day.",
  "platform.about.source":
    "A short summary, drawn from Wikipedia's article on the pow wow. Every community runs its own gathering its own way, and its page here is the authority on that one.",
  "platform.about.sourceLink": "Pow wow on Wikipedia",

  "platform.software.heading": "What the software does",
  "platform.software.intro":
    "Built around how a pow wow actually runs: outdoors, often once a year, led by volunteers, and dependent on a day that cannot simply be repeated.",
  "platform.software.schedule.title": "A weekend made of sessions",
  "platform.software.schedule.body":
    "Grand Entry, afternoon and evening competitions, intertribal time and payouts sit on the schedule separately, because a pow wow is not one deadline.",
  "platform.software.schedule.alt":
    "Demo event schedule showing several sessions across a pow wow weekend.",
  "platform.software.registration.title": "Registration on the community's own terms",
  "platform.software.registration.body":
    "A window per session rather than one deadline for the weekend, with the categories, the questions and the fees the community wrote for itself. A dancer registers from a phone and keeps a link to their own entry.",
  "platform.software.registration.alt":
    "Phone registration form with a category chosen automatically from date of birth.",
  "platform.software.judging.title": "Judging by the panel that turned up",
  "platform.software.judging.body":
    "Judges get a code at the drum, not an account weeks beforehand. A panel can change between sessions, scores go in on the judge's own phone, and names can be kept off the sheet where the community wants them off it.",
  "platform.software.judging.alt":
    "Judge scoring screen with categories, criteria and a sent sync state.",
  /* The lightbox. `enlarge` is a hint on the thumbnail and is hidden from assistive technology —
     the link already reads as the screenshot's alt text — while `close` labels a real control. */
  "platform.software.enlarge": "View full size",
  "platform.software.close": "Close",
  "platform.software.payouts.title": "Results, and then the prize money",
  "platform.software.payouts.body":
    "Placings worked out from the sheets as they come in, held back until someone approves them, published to the community's page — and carried through to who is owed what.",
  "platform.software.payouts.alt":
    "The payout queue as the treasurer sees it: still owed, paid out, and a Pay button beside each name.",
  "platform.software.prizes.title": "Prize rules the community wrote",
  "platform.software.prizes.body":
    "Flat payouts, placements, pooled amounts and per-session rules can all live beside each other without forcing the weekend into a preset.",
  "platform.software.prizes.alt":
    "Prize rule list showing several custom payout structures for a demo event.",
  "platform.software.offline.title": "The venue hub keeps working offline",
  "platform.software.offline.body":
    "When the internet drops, the local hub keeps check-in, registration and judging moving, then syncs the queued work when the connection returns.",
  "platform.software.offline.alt":
    "Venue hub screen showing no internet, local address, queued work and sync status.",
  "platform.software.desk.title": "A desk that works at the gate",
  "platform.software.desk.body":
    "Register someone at the table, check arrivals by code or by name, and see who is present, whether the venue Wi-Fi is working or not.",
  "platform.software.updates.title": "Updates that reach the people already registered",
  "platform.software.updates.body":
    "An announcement goes on the event page and out to whoever asked to hear about it: a delayed start, a change of arena, a lost child found.",
  "platform.software.paper.title": "Everything prints",
  "platform.software.paper.body":
    "Nothing important exists only on a screen. Programmes, judging sheets, check-in lists and confirmation slips all print, because a dead battery is not a reason for a competition to stop.",
  "platform.software.ownWords.title": "The community's own words",
  "platform.software.ownWords.body":
    "Colours, logo, the words for a dancer and a category, the languages the page answers in — settings, not code. Bringing a community on is a form, not a release.",

  /* The organiser's way in from the top of the page. Deliberately a line of text and a link
     rather than a button: they are a few readers a year, and the visitor is not one of them. */
  "platform.organisers.prompt": "Running a pow wow yourself?",
  "platform.organisers.promptLink": "See what the software does",

  "platform.organisers.heading": "Bringing your pow wow here",
  "platform.organisers.body":
    "Every community in the directory has its own public page, web address, staff accounts, event data, and permission boundaries. Information from one community is not visible to another. If your community runs a pow wow and wants to manage registration, schedules, judging, results, and payouts here, please contact us.",
  "platform.organisers.action": "Contact us",

  "site.sampleData.long":
    "This is sample data used for development. It is not a real event listing.",
  "site.sampleData.short": "Sample data — not a real event.",

  // Getting back somewhere.
  "nav.backToEvent": "Back to the event page",
  "nav.backToCommunity": "Back to {community}",
  "nav.eventPage": "The event page",

  // A community's front page.
  "notFound.title": "Page not found",
  "notFound.heading": "We could not find that page",
  "notFound.body":
    "The address may have been mistyped, or the page may have been taken down. Everything this community has published is linked from its home page.",
  "notFound.noCommunity":
    "This address does not belong to a community on this site. Check the web address on the poster or the link you followed.",

  "home.onNow": "On now",
  "home.comingUp": "Coming up",
  // The card's own way through to the event page. The heading is a link to the same place, but a
  // heading does not read as something to press — on a phone the three buttons under the summary
  // are the whole card as far as most visitors are concerned, and one of them has to be "the rest
  // of it". Short, because it sits between "Register now" and "Results".
  "home.eventDetails": "Details",
  // The community's own pictures at the top of its page, the counterpart of the platform strip.
  "home.hero.label": "Pictures from {community}",
  "home.hero.alt": "A picture from {community}",
  /* Section eyebrows on a community's page — the small line above a heading that tells a skimmer
     what kind of thing follows. Short by design: they sit above the heading, not instead of it. */
  "home.welcomeEyebrow": "Welcome",
  "home.updatesEyebrow": "Latest",
  "home.programmeEyebrow": "The programme",
  "home.contactEyebrow": "Contact",
  "home.thanksEyebrow": "Supported by",
  "home.recentUpdates": "Recent updates",
  "home.allUpdates": "All updates for {event} →",
  "home.noEvents": "No events have been published yet.",
  "home.nothingElse": "Nothing else is scheduled yet.",
  "home.finished": "Finished",
  "home.getInTouch": "Get in touch",
  "home.contactPending": "Contact details will be published here.",
  "home.communityWebsite": "{community}'s website",

  // The event page — ux.md §3.1.
  "event.cancelled.heading": "This event is cancelled",
  "event.cancelled.body":
    "Please do not travel for this event. Contact the organisers listed below if you have questions.",
  "event.addToCalendar": "Add to calendar",
  "event.updates": "Updates",
  "event.schedule": "Schedule",
  "event.schedule.pending": "The schedule will be announced.",
  // Stands alone, under the session's name. Deliberately not joined to the session's date and not
  // placed beside the registration badge: a Grand Entry is not something anyone registers for
  // (it is `enrolment: automatic`, D75), and a row reading "Grand Entry at 7:00 p.m. · Registration
  // opens at 2:00 p.m." was read as one sentence about the Grand Entry.
  "event.session.grandEntryTime": "{grandEntry} at {time}",
  "event.competitions.pending": "Competition details will be announced.",
  // The credits a poster carries — MC, arena director, host drum (D62). Deliberately not
  // "Staff" or "Officials": these are people the community is honouring by naming.
  "event.roster": "Who is leading it",
  "event.where": "Where it is",
  "event.openInMaps": "Open in maps",
  "event.timezoneNote": "All times are shown in {zone}, the time where the event is held.",

  // Images (D71). The poster's fallback alt names the event, because a poster with no description
  // written for it is still unambiguously a poster *for something*.
  "event.poster.alt": "Poster for {event}",
  "event.poster.fullSize": "View the full-size poster",
  "event.poster.group": "Posters for {event}",
  "event.updates.images": "Photos with the update: {title}",
  "event.updates.imagesHint": "Swipe or scroll sideways to see more.",

  // The full-screen view. Shared by the posters and the photos on an update, because tapping either
  // is the same gesture — "let me actually see that" — and two sets of words for it would be two.
  "image.enlarge": "Tap to enlarge",
  "image.close": "Close",
  "image.original": "Open the original",

  // Headings over the community's own `info_json` prose. The heading is ours; the body is theirs.
  "event.info.generalRules": "Rules",
  "event.info.registrationInstructions": "How to register",
  "event.info.parking": "Parking",
  "event.info.travel": "Getting there",
  "event.info.accommodation": "Where to stay",
  "event.info.accessibility": "Accessibility",
  "event.info.emergency": "In an emergency",
  "event.info.cancellation": "If the event is cancelled",

  "announcement.pinned": "Pinned",
  "announcement.urgent": "Urgent",
  "announcement.important": "Important",

  "sponsors.heading": "With thanks to",

  "contact.whoToAsk": "Who to ask",
  "contact.askAtDesk": "Ask at the registration desk at the gate.",
  "contact.questions": "Questions: {contact}",
  "contact.pleaseContact": "Please contact {contact} for details.",

  // Registering — ux.md §3.2.
  "registration.pageTitle": "Register — {event}",
  "registration.pageDescription": "Register for {event}",
  "registration.action.register": "Register",
  "registration.action.now": "Register now",
  "registration.robotCheck": "One quick check that you are not a robot.",
  "registration.submit": "Send my registration",
  "registration.aboutYou": "About you",
  "registration.permissions": "Permissions",
  "registration.terms": "Terms",
  "registration.noAccountNote":
    "You do not need an account. After you send this you will get your {dancerNumber} and a link that lets you change or cancel your registration.",
  "registration.qrTitle": "Your registration code",
  "registration.status.label": "Status",
  "registration.status.active": "Active",
  "registration.status.cancelled": "Cancelled",
  "registration.registeredAt": "Registered",

  // The core block — shown only where the community's own form does not already ask.
  "registration.core.firstName": "First name",
  "registration.core.lastName": "Last name",
  "registration.core.communityAffiliation": "Home community",
  "registration.core.communityAffiliation.help":
    "Start typing and pick from the list, or write it in yourself — anywhere is fine.",
  "registration.core.age": "Age",
  "registration.core.age.help":
    "How old you are at the pow wow. Used to work out which age {category} you belong in — you can change that choice.",
  "registration.core.phone": "Phone number",
  "registration.core.phone.help": "So we can reach you about your registration.",
  "registration.core.email": "Email",

  // More of the four honest states than `registration.state.*` above already carries.
  "registration.state.opensLater": "Registration opens later",
  "registration.state.opensOn": "Registration opens {day} at {time}",
  "registration.state.closesAtTime": "Registration closes at {time}",
  "registration.state.closesOn": "Registration closes {day} at {time}.",
  // Two keys rather than one with a suffix: this layer substitutes, it does not pluralise.
  "registration.state.closesInMinute": "Registration closes in {minutes} minute",
  "registration.state.closesInMinutes": "Registration closes in {minutes} minutes",
  "registration.state.openUntil": "Registration is open until {time}",
  "registration.state.notOpen": "Registration is not open.",
  "registration.state.notOpenForEvent": "Registration is not open for this event.",
  // The same four states again, short enough to sit in a list beside a competition's name.
  "registration.state.short.opensLater": "Opens later",
  "registration.state.short.notScheduled": "Times to be announced",
  "registration.state.short.closed": "Closed",

  "registration.closed.heading": "Registration is closed",
  "registration.closed.body":
    "Registration is closed — see the desk at the gate. Staff can still register you.",
  "registration.closed.deskCanRegister":
    "Staff at the registration desk can still enter you on the day. Come to the gate and ask.",
  "registration.notYetOpen.comeBack":
    "Come back when registration opens, or ask at the desk on the day.",
  "registration.notOnline.heading": "Registration is not online for this event",
  "registration.notOnline.body":
    "Register in person at the desk at the gate. Staff can enter your details for you.",

  // The progress indicator. Written by the enhancement script, so these travel to it as data.
  "registration.progress.empty": "Nothing filled in yet",
  "registration.progress.complete": "Everything needed is filled in",
  "registration.progress.count": "{done} of {total} things done",

  // Choosing what to enter — ux.md §3.3.
  "registration.entries.legend": "What are you entering?",
  "registration.entries.help":
    "Choose everything you are taking part in. We work out your age {categories} from the age you gave where we can — you can change any of those choices.",
  "registration.entries.chooseOne": "Choose at least one {competition} to enter.",
  "registration.entries.enterCompetition": "Enter {name}",
  "registration.entries.count": "{entered} of {limit} entered",
  "registration.entries.full": "Full",
  "registration.entries.closed": "Closed",
  // Narrowing a long list of choices. A competition that runs in several sessions has a box per
  // session, so a programme of a dozen categories becomes fifty boxes — written by the enhancement
  // script, so these travel to it as data.
  "registration.entries.filter.label": "Search this list",
  "registration.entries.filter.placeholder": "e.g. Saturday afternoon",
  "registration.entries.filter.showing": "Showing {shown} of {total}.",
  "registration.entries.filter.none": "Nothing matches “{query}”. Clear the box to see them all.",
  "registration.entries.filter.keptChosen":
    "Showing {shown} of {total}, including what you have already chosen.",
  "registration.entries.heading": "Entered",
  "registration.entries.empty": "Nothing entered yet.",
  "registration.entries.categoryPending": "{category} to be confirmed at the desk",

  "entry.status.entered": "Entered",
  "entry.status.withdrawn": "Withdrawn",
  "entry.status.noShow": "Marked as not arrived",
  "entry.status.disqualified": "Disqualified",

  // One labelled control. The marks and the empty option are the software's, not the organiser's.
  "field.required": "(required)",
  "field.optional": "(optional)",
  "field.choose": "Choose…",
  "field.error.required": "This is needed.",
  "field.error.consentRequired": "This has to be agreed to before registering.",
  "field.error.email": "That does not look like an email address.",
  "field.error.phone": "That does not look like a phone number.",
  "field.error.date": "Use a date like 1990-06-21.",
  "field.error.number": "This has to be a number.",

  // The offered answers on a free-text field — the home-community list (D46). Every one of these
  // says, in one way or another, that the list is a shortcut and not a set of permitted answers.
  "field.suggest.open": "Show the list",
  "field.suggest.hint": "Pick one from the list, or type your own answer.",
  "field.suggest.showing": "Showing {shown} of {total}.",
  "field.suggest.none": "Nothing on the list matches “{query}”. What you type is kept.",
  "field.suggest.useTyped": "Use “{value}” — not on the list",

  // The confirmation slip — a piece of paper that happens to be a web page (D21).
  "confirmation.pageTitle": "You are registered — {event}",
  "confirmation.heading": "You are registered",
  "confirmation.badge": "Registered.",
  "confirmation.printNote":
    "Print this page or keep the link below — you will need the code at the gate.",
  "confirmation.missing.heading": "We cannot show your confirmation",
  "confirmation.missing.body": "This page needs the link that was created when you registered.",
  "confirmation.missing.help":
    "If you have already registered, use the link from your confirmation. If you are not sure, the desk at the gate can look you up.",
  "confirmation.beforeYouCome": "Before you come",

  // Team self-registration — one captain enters the whole roster (D57).
  "team.options.heading": "Registering a team?",
  "team.options.help":
    "Some competitions are entered as a team. Pick one to register the whole team in a single form.",
  "team.options.rosterNote": "{min}–{max} members",
  "team.options.register": "Register a team",
  "team.backToIndividual": "← Register as an individual instead",
  "team.about.legend": "Your team — {competition}",
  "team.about.help": "Tell us about the team, then add each member below.",
  "team.field.name": "Team name",
  "team.field.contactPhone": "Contact phone",
  "team.field.contactEmail": "Contact email",
  "team.field.session": "Session",
  "team.member.legend": "Member {n}",
  "team.member.captain": "This member is the captain",
  "team.member.add": "Add another member",
  "team.member.remove": "Remove this member",
  "team.roster.status": "{n} members — this team needs {min} to {max}",
  "team.submit": "Send our registration",
  "team.noAccountNote":
    "You do not need an account. After you send this, the team gets its number and every member is registered.",
  "team.error.notOpen":
    "Registration for that team competition is not open. Staff at the desk can still register the team.",
  "team.error.tooFew": "This team needs at least {min} members.",
  "team.error.tooMany": "This team can have at most {max} members.",
  "team.confirmation.pageTitle": "Your team is registered — {event}",
  "team.confirmation.heading": "Your team is registered",
  "team.confirmation.badge": "Registered.",
  "team.confirmation.numberLabel": "Team number",
  "team.confirmation.deskNote":
    "Show this at the registration desk when your team arrives. Each member is registered.",
  "team.confirmation.missing.heading": "We cannot show your team confirmation",
  "team.confirmation.missing.body":
    "This page needs the link that was created when the team registered.",

  // `/manage/{token}` — a participant's own registration, with no account.
  "manage.pageTitle": "Your registration — {name}",
  "manage.heading": "Your registration",
  "manage.action": "Manage my registration",
  "manage.yourLink": "Your link:",
  "manage.linkNote":
    "Keep this. It is how you change or cancel your registration, and it is what the code above contains.",
  "manage.printNote":
    "Use your browser's Print option to keep a paper copy. The code above opens this page.",
  "manage.checkInNote":
    "This is not the code the gate scans. That one is in your confirmation email — open “Show my check-in code” there. If you cannot find it, staff at the desk can look you up by name.",
  "manage.notFound.heading": "We could not find that registration",
  "manage.notFound.body":
    "The link may have been mistyped, or it may belong to a different community's page. Check the link on your confirmation, or ask at the registration desk at the gate — they can look you up by name.",
  "manage.entries.heading": "What you are entered in",

  "manage.withdraw.action": "Withdraw",
  "manage.withdraw.confirm": "Withdraw from {what}?",
  "manage.withdraw.warning":
    "This cannot be undone here. Staff at the desk can enter you again if you change your mind.",
  "manage.withdraw.reason": "Why are you withdrawing?",
  "manage.withdraw.reasonHelp": "Optional. It helps the organisers plan.",
  "manage.withdraw.confirmAction": "Yes, withdraw me",
  "manage.withdraw.keep": "Keep my entry",
  "manage.withdraw.done": "That entry is withdrawn.",
  "manage.withdraw.disabled":
    "Online withdrawal is not enabled for this event. The registration desk can do it for you.",

  "manage.addEntry.heading": "Enter something else",
  "manage.addEntry.label": "Which {competition}?",
  "manage.addEntry.action": "Add it",
  "manage.addEntry.choose": "Choose something from the {competitions} first.",
  "manage.addEntry.done": "That is added to your registration.",

  "manage.contact.heading": "Your contact details",
  "manage.contact.saved": "Your contact details are saved.",
  "manage.contact.save": "Save my details",
  "manage.contact.address": "Street address",
  "manage.contact.city": "Town or city",
  "manage.contact.region": "Province or territory",
  "manage.contact.postalCode": "Postal code",
  "manage.contact.country": "Country",
  "manage.contact.emergencyName": "Emergency contact",
  "manage.contact.emergencyPhone": "Emergency contact phone",
  "manage.contact.disabled":
    "Online editing is not enabled for this event. The registration desk can change these details for you.",
  "manage.contact.registrarNote":
    "Your name, your age and your {category} are the registrar's to correct — ask at the desk if one of them is wrong.",

  // `/c/{code}` — the check-in page, and the code the gate scans (D66).
  "selfCheckIn.pageTitle": "Your check-in code — {name}",
  "selfCheckIn.heading": "Your check-in code",
  "selfCheckIn.showThis": "Show this at the gate. Staff scan it to mark you here.",
  "selfCheckIn.codeLabel": "Your code, if the camera will not read the square:",
  "selfCheckIn.link": "Your check-in page:",
  "selfCheckIn.printNote":
    "Use your browser's Print option to keep a paper copy. Bring it, or bring your phone.",
  "selfCheckIn.notFound.heading": "We could not find that check-in code",
  "selfCheckIn.notFound.body":
    "The code may have been mistyped, or it may belong to a different community's page. Ask at the gate — staff can look you up by name or by your registration number.",
  "selfCheckIn.entries.heading": "What you are checking in for",
  "selfCheckIn.nothingToday": "You are not entered in anything for this one.",
  "selfCheckIn.staffOnly":
    "Check-in for this event is done by staff at the gate. Show the code above when you arrive.",
  "selfCheckIn.chooseSession": "Which one are you here for?",
  "selfCheckIn.chooseCompetition": "Which one are you checking in for?",
  "selfCheckIn.action": "Check me in",
  "selfCheckIn.done": "You are checked in for {session}.",
  "selfCheckIn.already": "You were already checked in for {session}.",
  "selfCheckIn.undoNote":
    "If this was a mistake, tell someone at the desk — undoing a check-in is theirs to do, because it is what the honoraria are worked out from.",
  "selfCheckIn.failed": "That did not go through. Try again, or check in at the gate.",
  "selfCheckIn.needYourCode":
    "This phone has not opened your check-in code yet, so we cannot tell who you are. Open “Show my check-in code” in your confirmation email once; after that, this poster can check you in with one tap. If you cannot find the email, staff at the gate can check you in.",

  "manage.survey.heading": "Tell the organisers how it went",
  "manage.survey.body": "A few questions, and it helps them plan next year.",
  "manage.survey.action": "Answer the survey",

  "manage.documents.heading": "Documents",
  "manage.documents.none": "Nothing outstanding.",
  "manage.documents.pending": "Still to be checked:",
  "manage.documents.notVerified": "Not yet verified",
  "manage.documents.uploadNote":
    "Documents cannot be uploaded here yet. Bring anything outstanding to the registration desk at the gate and staff will record it.",

  // The public leaderboard — ux.md §3.8.
  "results.title": "Results",
  "results.pageTitle": "Results — {event}",
  "results.pageDescription": "Published results for {event}",
  "results.published": "Results have been published →",
  "results.unavailable.heading": "Results are not available right now",
  "results.unavailable.body": "Please try again shortly, or ask at the {mc}'s table.",
  "results.empty.heading": "Nothing published yet",
  "results.empty.body":
    "Results appear here once the scoring for each {category} has been checked and approved. That happens through the day rather than all at once, so it is worth looking again later.",
  "results.empty.category": "Nothing published for this {category}.",
  "results.byNumberNote": "This community publishes results by {dancerNumber} rather than by name.",
  "results.column.place": "Place",
  "results.column.name": "Name",
  "results.column.score": "Score",
  "results.adjusted": "Adjusted",
  "results.tieBreak": "Tie-break",
  "results.adjustedNote":
    "An organiser changed this placing after the calculation, with a recorded reason.",
  "results.tieBreakNote": "This placing was settled by the {competition}'s tie-break rule.",

  // A public survey response page.
  "survey.title": "Survey",
  "survey.pageTitle": "Survey — {community}",
  "survey.closed.heading": "This survey is not open",
  "survey.closed.empty": "There is nothing to answer here.",
  "survey.closed.body": "It may have closed, or it may not have opened yet.",
  "survey.thanks": "Thank you.",
  "survey.recorded": "Your answers are recorded.",
  "survey.anonymous":
    "This survey is anonymous. Your answers are not recorded against your name or your registration.",
  "survey.attributed":
    "Your answers are recorded against your registration so the organisers can follow up.",
  "survey.submit": "Send my answers",

  // Email. Every message the system sends says who it is from and what it is for, in the language
  // the reader asked for — an email is often the only surface a person sees twice.
  "email.footer": "Sent by {community} via PowWow.",
  "email.buttonFallback": "If the button does not work, paste this into your browser:",

  "email.magicLink.subject": "Your sign-in link for {community}",
  "email.magicLink.heading": "Sign in",
  "email.magicLink.body":
    "Use the button below to sign in. The link works once and expires in 15 minutes.",
  "email.magicLink.action": "Sign in",
  "email.magicLink.unexpected":
    "If you did not ask to sign in, you can ignore this — nobody can use the link but you.",

  "email.tempPassword.subject": "Your temporary password for {community}",
  "email.tempPassword.heading": "Your temporary password",
  "email.tempPassword.body":
    "Sign in with this password. You will be asked to choose a new one straight away.",
  "email.tempPassword.action": "Sign in",
  "email.tempPassword.unexpected":
    "This password expires shortly and can only be used once. If you did not expect it, tell whoever runs your events.",

  "email.passwordReset.subject": "Reset your password for {community}",
  "email.passwordReset.heading": "Reset your password",
  "email.passwordReset.body":
    "Use the button below to choose a new password. The link works once and expires in 30 minutes.",
  "email.passwordReset.action": "Choose a new password",
  "email.passwordReset.unexpected":
    "If you did not ask for this, ignore it — your password has not changed.",

  "email.invitation.subject": "You have been invited to {community}",
  "email.invitation.heading": "Join {community}",
  "email.invitation.body": "You have been invited to help run events for {community}.",
  "email.invitation.action": "Accept the invitation",

  "email.registration.subject": "You are registered for {event}",
  "email.registration.body": "{name} is registered. Here is everything from the confirmation slip.",
  "email.registration.bodyNoName":
    "You are registered. Here is everything from the confirmation slip.",
  "email.registration.team": "Registered with {team}.",
  "email.registration.checkIn.action": "Show my check-in code",
  // Stands in for the symbol wherever a mail client blocks the image, so it says what to do rather
  // than naming the thing that is missing. "QR code" would be an accurate label and useless advice.
  "email.registration.checkIn.qrAlt":
    "Your check-in code as a square barcode. If it does not appear, use the button below.",
  "email.registration.checkIn.note":
    "Open this at the gate and staff scan the square on your screen. Your code is {code} — read it out if the camera will not focus.",
  "email.registration.keepThis":
    "Keep this email — the desk can find you by name, and the link above is how you change anything.",

  "email.checkIn.subject": "You are checked in at {event}",
  "email.checkIn.heading": "Checked in",
  "email.checkIn.body": "{name} is checked in for {session} at {at}.",
  "email.checkIn.bodyNoName": "You are checked in for {session} at {at}.",

  "email.announcement.subject": "An update from {community}",
};
